import { useEffect, useState } from 'react'
import { t } from '../lib/i18n'
import config from '../../studio42.config.json'
import { inIosShell } from '../lib/native'

/**
 * "Add to home screen."
 *
 * For an app that is not on a store yet — which is most of them, for most of
 * their life. A web app on a home screen gets its own icon, no browser chrome
 * and a place in the app switcher, and almost nobody discovers that on their own.
 *
 * ── Two platforms, two completely different jobs ─────────────────────────────
 *
 * Chrome and Edge fire `beforeinstallprompt`, which can be saved and replayed
 * from a button. That is the easy half.
 *
 * **iOS Safari fires nothing and has no API at all.** Installing is Share →
 * Add to Home Screen, done by hand, and the only thing software can do is say
 * so. A prompt that shows an Install button doing nothing on iPhone is worse
 * than no prompt, so iOS gets instructions instead.
 */

interface InstallEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED = 's42_install_dismissed'

function isStandalone(): boolean {
  // The iOS-only property is the reason for the cast; Safari has never
  // implemented display-mode for this.
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
    // iPadOS reports as a Mac, and the touch points are what give it away.
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export default function InstallPrompt() {
  const [event, setEvent] = useState<InstallEvent | null>(null)
  const [showIos, setShowIos] = useState(false)
  const [gone, setGone] = useState(true)

  useEffect(() => {
    /*
     * Not shown when the app is already installed, when it has been dismissed
     * before, or when this project ships on a store — in which case the store
     * listing is the better answer and two install routes just confuse people.
     */
    if (isStandalone()) return
    // Inside the iOS app it IS installed — a WKWebView is never "standalone",
    // so without this the App Store app would tell people to add it to the
    // Home Screen.
    if (inIosShell()) return
    if (localStorage.getItem(DISMISSED)) return
    if (config.mobile?.onStore) return

    setGone(false)

    if (isIos()) {
      // No event will ever arrive. Wait a moment so this is not the first thing
      // a visitor sees before the page has even rendered.
      const timer = setTimeout(() => setShowIos(true), 4000)
      return () => clearTimeout(timer)
    }

    const onPrompt = (e: Event) => {
      // Chrome shows its own bar unless this is called; we want it on our
      // terms, in our words, at a moment that makes sense.
      e.preventDefault()
      setEvent(e as InstallEvent)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', () => setGone(true))
    return () => window.removeEventListener('beforeinstallprompt', onPrompt)
  }, [])

  function dismiss() {
    // Remembered across visits. An install prompt that returns every time is an
    // advert, and people learn to close it without reading.
    localStorage.setItem(DISMISSED, String(Date.now()))
    setGone(true)
  }

  if (gone || (!event && !showIos)) return null

  return (
    <div className="install-prompt" role="complementary">
      <div>
        <strong>{t('install.title')}</strong>
        <p className="muted small">
          {showIos ? t('install.ios') : t('install.why')}
        </p>
      </div>
      <span className="row">
        {event && (
          <button className="primary" onClick={async () => {
            await event.prompt()
            const { outcome } = await event.userChoice
            // Either way the moment has passed: accepted means installed,
            // dismissed means asked and answered.
            if (outcome === 'dismissed') dismiss()
            setEvent(null)
          }}>{t('install.add')}</button>
        )}
        <button onClick={dismiss}>{t('install.no')}</button>
      </span>
    </div>
  )
}
